import type { PanelState } from "@kundeninfo/ui";
import { forgetValue, readJson, rememberValue } from "./browserMemory.js";

/**
 * Der Randzustand liegt im Browser, NICHT in der Datenbank. Grund: Er ist eine
 * geräteabhängige Bequemlichkeit — dasselbe Benutzerkonto sitzt mal am Desktop (wo beide
 * Ränder ohnehin dauerhaft stehen), mal am iPad (wo sie geklappt werden). Ein in der
 * Datenbank gemerkter Zustand würde vom einen Gerät auf das andere überschwappen und dort
 * das Falsche wiederherstellen. Was in der Datenbank steht, ist deshalb nur der Schalter
 * `rememberPanels` selbst — die Absicht des Benutzers, nicht der Zustand.
 */
export const SPEICHER_SCHLUESSEL = "ki-randbereiche";

/**
 * Standardzustand des Randes, wenn nichts (Brauchbares) gemerkt ist. Er gilt für jede
 * Größenklasse, weil ihn nur eine davon überhaupt auswertet: Auf dem Desktop steht der
 * Rand unabhängig von diesem Wert dauerhaft offen, auf dem Telefon gibt es ihn gar nicht.
 * Bleibt das iPad — dort startet der Kontext eingeklappt, weil Liste und Kontext zusammen
 * wenig Platz für die Akte lassen (siehe KONTEXT_SCHWELLE in MeSchale.tsx); er lässt sich jederzeit
 * aufklappen.
 */
export const STANDARD_PANELS: PanelState = { context: false };

/*
 * Seit dem 10.09.2026 steht hier nur noch `context`. Ein älterer gemerkter Wert trägt
 * zusätzlich `nav` (die Navigationsspalte, die es nicht mehr gibt) — er bleibt gültig und
 * wird beim Lesen um das überzählige Feld beschnitten: Wer seinen Kontext eingeklappt
 * hatte, soll ihn nicht deshalb wieder offen vorfinden, weil anderswo eine Spalte
 * weggefallen ist.
 */
function istPanelState(wert: unknown): wert is PanelState {
  if (typeof wert !== "object" || wert === null) return false;
  const kandidat = wert as { context?: unknown };
  return typeof kandidat.context === "boolean";
}

/**
 * Der zuletzt gemerkte Randzustand, oder null, wenn keiner vorliegt. Der Wert kommt aus dem
 * Browser: Er kann fehlen, aus einer älteren Fassung stammen oder von Hand verändert worden
 * sein — deshalb wird er geprüft, bevor er zurückgegeben wird, statt blind übernommen.
 *
 * Jeder Zugriff steht in try/catch: In einem privaten Fenster wirft bereits das LESEN
 * (der Zugriff auf `localStorage` selbst, nicht erst getItem). Fehlt der Wert oder wirft
 * der Zugriff, gilt der Standard — das Merken ist eine Bequemlichkeit, kein Muss.
 */
export function lesePanels(): PanelState | null {
  const geparst = readJson(SPEICHER_SCHLUESSEL);
  return istPanelState(geparst) ? { context: geparst.context } : null;
}

/** Merkt den Randzustand für den nächsten Start. Scheitert das, bleibt es folgenlos. */
export function merkePanels(panels: PanelState): void {
  rememberValue(SPEICHER_SCHLUESSEL, JSON.stringify({ context: panels.context }));
}

/**
 * Verwirft den gemerkten Randzustand. Wird beim Ausschalten des Schalters gerufen, damit
 * ein späteres Einschalten nicht einen längst überholten Zustand wiederbelebt.
 */
export function vergissPanels(): void {
  forgetValue(SPEICHER_SCHLUESSEL);
}
